import { formControlClasses } from '@mui/material';
import {
  LocalizationProvider,
  DatePicker,
  pickersInputBaseClasses,
  pickersSectionListClasses,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Moment } from 'moment';
import React, { forwardRef, ReactNode, Ref, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { ISO_LOCAL_DATE } from '../../../../../shared/utils/dateTime';
import { slotInputStyle } from '../../styledComponentStyle';

import './DateFieldSlot.css';
import { SlotWrapper, SlotWrapperProps } from './SlotWrapper';
import { useEnrollSlotRef } from './hooks';
import { SlotRef } from './types';

export const StyledDateField = styled(DatePicker)<{
  $width?: string;
  $fullWidth?: boolean;
}>`
  &.${formControlClasses.root} {
    ${p => p.$width && `width: ${p.$width};`}
    ${slotInputStyle}

    & .${pickersInputBaseClasses.root} {
      color: var(--HPACS_Text_Light_Grey, #a3a6ab);
      height: 37px;
      box-sizing: border-box;
      padding-top: 7px;
      padding-bottom: 7px;
      font-size: 14px;
      line-height: 24px;

      & fieldset {
        border: 1px solid #e7e7e7;
      }
      & button {
        color: #5a6473;
      }

      & .${pickersSectionListClasses.root} {
        justify-content: center;
      }
    }
  }
`;
interface DateFieldSlotProps extends SlotWrapperProps {
  format?: string;
  date?: Moment | null;
  onChangeDate: (value: Moment | null) => void;
  readOnly?: boolean;
  children?: ReactNode;
}
export const DateFieldSlot = forwardRef(
  (
    {
      format,
      date,
      onChangeDate,
      readOnly,
      children,
      ...slotWrapperProps
    }: DateFieldSlotProps,
    ref: Ref<SlotRef>,
  ) => {
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    useEnrollSlotRef({ ref, wrapperRef, inputs: [inputRef.current] });
    const { t: globalT, i18n } = useTranslation('translation');
    const dateFormatKey = 'common.dateFormatInputString';
    format =
      format ||
      (i18n.exists(dateFormatKey)
        ? `${globalT(dateFormatKey)}`
        : ISO_LOCAL_DATE);

    return (
      <SlotWrapper ref={wrapperRef} {...slotWrapperProps}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <StyledDateField
            format={format}
            views={['year', 'month', 'day']}
            value={date}
            inputRef={inputRef}
            onChange={value =>
              value?.isValid() ? onChangeDate(value) : onChangeDate(null)
            }
            readOnly={readOnly}
          />
        </LocalizationProvider>
        {children}
      </SlotWrapper>
    );
  },
);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const DateFieldWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ParticleText = styled.span<{ $margin?: string }>`
  flex-grow: 1;
  margin: ${p => p.$margin ?? '0px'};
  white-space: nowrap;
  color: #5a6473;
  text-align: center;
`;

interface PeriodSlotProps extends SlotWrapperProps {
  format?: string;
  startDate?: Moment | null;
  setStartDate?: (date: Moment | null) => void;
  endDate?: Moment | null;
  setEndDate?: (date: Moment | null) => void;
}
export const PeriodSlot = forwardRef(
  (
    {
      format,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      ...slotWrapperProps
    }: PeriodSlotProps,
    ref: Ref<SlotRef>,
  ) => {
    const wrapperRef = useRef(null);
    const startInputRef = useRef(null);
    const endInputRef = useRef(null);
    useEnrollSlotRef({
      ref,
      wrapperRef,
      inputs: [startInputRef.current, endInputRef.current],
    });

    const { t: globalT } = useTranslation('translation');
    format = format || `${globalT('common.dateFormatInputString')}`;
    const { t } = useTranslation('translation', {
      keyPrefix: 'features.imageList.myImageList.searchDetails',
    });

    return (
      <SlotWrapper ref={wrapperRef} {...slotWrapperProps}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Wrapper>
            <DateFieldWrapper>
              <Trans
                t={t}
                i18nKey={'slots.imageDate.start'}
                components={[
                  <ParticleText $margin={'0 5px 0 0'} />,
                  <ParticleText $margin={'0 0 0 5px'} />,
                  <StyledDateField
                    $width={'80%'}
                    format={format}
                    views={['year', 'month', 'day']}
                    value={startDate}
                    onChange={value => setStartDate?.(value)}
                  />,
                ]}
              />
            </DateFieldWrapper>
            <DateFieldWrapper>
              <Trans
                t={t}
                i18nKey={'slots.imageDate.end'}
                components={[
                  <ParticleText $margin={'0 5px 0 0'} />,
                  <ParticleText $margin={'0 0 0 5px'} />,
                  <StyledDateField
                    $width={'80%'}
                    format={format}
                    views={['year', 'month', 'day']}
                    value={endDate}
                    onChange={value => setEndDate?.(value)}
                  />,
                ]}
              />
            </DateFieldWrapper>
            {/* 기간 이상하다는 문구 */}
          </Wrapper>
        </LocalizationProvider>
      </SlotWrapper>
    );
  },
);
